import {FC} from 'react'
import {SearchForm} from './parts/SearchForm/SearchForm'
import {AlertList} from './parts/AlertList/AlertList'
import {
  ALERT_TYPES,
  AlertTypeQueryResult,
  EMPLOYEE_ALERTS,
  SearchAlertQuery,
  SearchAlertQueryResult,
  SortColumn,
  Order,
} from './query'
import {CsvDownloadModal} from './parts/CsvDownloadModal/CsvDownloadModal'
import {LoadingPage} from 'components/ui/LoadingPage'
import {Tab} from 'components/ui/Tab'
import {Content} from 'components/layout/Content'
import {SearchResult} from 'components/ui/SearchResult'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {useQuery} from 'hooks/graphql'
import {PerPage} from 'components/ui/PerPage'
import {PaginatorWithResult} from 'jbc-front/components/SearchForm'
import styles from './Presenter.scss'
import classnames from 'classnames'
import {useHistory, useLocation} from 'react-router-dom'
import {useDisplayAlertLimit} from './limit'
import {
  convertFormToSearchParams,
  convertSearchParamsToForm,
  convertSearchParamsToGraphQL,
  convertSearchParamsToQueryString,
  defaultSearchParams,
  getSearchParamsFromLocation,
  hasConditions,
} from './search'

export const Presenter: FC = () => {
  const location = useLocation()
  const history = useHistory()
  const [limit, setLimit] = useDisplayAlertLimit()
  const searchParams = {...getSearchParamsFromLocation(location), limit}
  const [sortColumn, sortOrder] = (searchParams.sort_type || defaultSearchParams.sort_type).split('__') as [
    SortColumn,
    Order,
  ]
  const searchFormValues = convertSearchParamsToForm(searchParams)

  const {loading: alertTypesLoading, data: alertTypesData} = useQuery<AlertTypeQueryResult>(ALERT_TYPES)

  const searchVariables = convertSearchParamsToGraphQL(searchParams)
  const {loading, data, previousData, refetch} = useQuery<SearchAlertQueryResult, SearchAlertQuery>(EMPLOYEE_ALERTS, {
    fetchPolicy: 'network-only',
    variables: searchVariables,
  })
  const totalCount =
    loading && previousData ? previousData.employeeAlerts.totalCount : data?.employeeAlerts?.totalCount || 0

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
      history.push({search: convertSearchParamsToQueryString({...searchParams, sort_type: `${column}__${nextOrder}`})})
    } else {
      history.push({search: convertSearchParamsToQueryString({...searchParams, sort_type: `${column}__desc`})})
    }
  }

  return (
    <div>
      {alertTypesLoading ? (
        <LoadingPage />
      ) : (
        <div>
          <div className={classnames(styles.tabsWrap, 'u-pb0')}>
            <div className={styles.tabs}>
              <Tab to="/system_announces">お知らせ一覧</Tab>
              <Tab to="/notifications">通知一覧</Tab>
              <Tab active>アラート一覧</Tab>
            </div>
          </div>

          <Content size="xxl">
            <div className={styles.csvModalButtonWithSearch}>
              <CsvDownloadModal search={searchVariables.search} hasConditions={hasConditions(searchFormValues)} />
              <div />
              <SearchForm
                values={searchFormValues}
                onSubmit={(values) => {
                  const {_page, ...newSearchParams} = convertFormToSearchParams(values) // reset page
                  return history.push({search: convertSearchParamsToQueryString(newSearchParams)})
                }}
                onClear={() => history.push({search: ''})}
                hasConditions={hasConditions}
                alertTypeList={alertTypesData?.alertTypes?.list ?? []}
              />
            </div>
            {loading ? (
              <LoadingPage />
            ) : (
              <div>
                <div className={styles.sortingFieldsWithResult}>
                  <div className={styles.searchResultWrap}>
                    <SearchResult
                      total={totalCount}
                      page={Number(searchParams._page)}
                      limit={Number(limit)}
                      failureMessage=""
                    />
                  </div>
                  <div className={styles.perPageWrap}>
                    <PerPage selected={Number(limit)} onChange={(per) => setLimit(String(per))} small />
                  </div>
                </div>

                <Spacer direction="y" size={25} />

                <AlertList
                  alerts={data?.employeeAlerts?.list ?? []}
                  onSort={handleSort}
                  order={sortOrder}
                  sortColumn={sortColumn}
                  handleRefetch={refetch}
                />

                <Spacer direction="y" size={20} />

                <PaginatorWithResult
                  limit={Number(limit)}
                  count={totalCount}
                  page={Number(searchParams._page)}
                  isLoading={alertTypesLoading || loading}
                  onClick={(page) =>
                    history.push({search: convertSearchParamsToQueryString({...searchParams, _page: String(page)})})
                  }
                  failureMessage="アラートがありません"
                />
              </div>
            )}
          </Content>
        </div>
      )}
    </div>
  )
}
